import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Text,
  Group,
  Divider,
  Box,
  TextInput,
  UnstyledButton,
  NumberInput,
  Modal,
  Switch,
  useMantineTheme
} from "@mantine/core";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import FilterSort from './FilterSort';
import FilterOptions from './FilterOptions';
import FilterVisibility from './FilterVisibility';
import FilterIconSelector from './FilterIconSelector';
import FilterPropertySelector from './FilterPropertySelector';

import SpaceMemberFiltersModel from '../../../../models/SpaceMemberFilters';
import SpaceFilterSettingModel from "../../../../models/SpaceFilterSetting";

import { useDebounce } from "../../../hooks/useDebounce";
import {
  ALL_FILTER_OPTIONS,
  ALL_FILTER_MEMBER_VISIBILITY,
  ALL_FILTER_SPACE_VISIBILITY,
  ALL_FILTER_MEMBER_FILE_VISIBILITY,
  ALL_FILTER_SPACE_FILE_VISIBILITY,
  ALL_FILTER_PROPERTIES,
  generateUniqueName,
  generateKeyFromName,
  getOptionKey,
  sortByName,
  getNextAvailableColor
} from "../../../../utils/Filters";

export default function FilterDefinitionEditor({
  slug,
  type,
  allFilters,
  handlers,
  selectedProperty,
  setSelectedProperty,
  onClose
}) {
  const theme = useMantineTheme();
  const [activeTypeLabel, setActiveTypeLabel] = useState(null);

  const colors = useSelector((state) => state.colors);

  const FilterModel = type === "filter-members" ? SpaceMemberFiltersModel : SpaceFilterSettingModel;

  const imageFilterWithThumbnail = allFilters.find((item) => item.format === "image" && item.options?.as_thumbnail === true);

  const handleAddProperty = (option) => {
    if (!option || !slug) return;

    const filter_name = generateUniqueName(option.label, allFilters);
    const filter_key = generateKeyFromName(filter_name);
    const option_key = getOptionKey(option.value);
    const isFile = ["image", "file"].includes(option.value);

    let visibility = type === "filter-members" ? ["card", "profile"] : ["creation", "card", "page"];
    if (isFile) {
      visibility = visibility.filter((item) => item !== "card");
    }

    const params = {
      name: filter_name,
      sort_order: allFilters.length,
      key: filter_key,
      format: option.value,
      options: option.options ? option.options : {
        [option_key]: ALL_FILTER_PROPERTIES[option.value]?.default_value || "",
        values: []
      },
      visibility: visibility,
      emojicon: option.emojicon
    };

    FilterModel.onCreate(
      slug,
      params,
      (data) => {
        if (data?.id) {
          handlers.append(data);
          setSelectedProperty(data);
        }
      },
      (error) => {
        const errors = Array.isArray(error?.errors) ? error?.errors : [];
        const errorsMessages = errors.join(', ');
        Swal.fire("Error", errorsMessages, "error");
      }
    );
  };

  const onFilterUpdate = (id, params) => {
    FilterModel.onUpdate(
      slug,
      id,
      params,
      (data) => {
        console.log("UPDATED FILTER:: ", data);
        if (data?.id) {
          handlers.setState((current) =>
            current.map((item) => item.id === data.id ? data : item)
          );
        }
      },
      (error) => {
        const errors = Array.isArray(error?.errors) ? error?.errors : [];
        const errorsMessages = errors.join(', ');
        Swal.fire("Error", errorsMessages, "error");
      }
    );
  };

  const handleDelete = () => {
    const htmlMembers = "All member data and any associated survey questions will be permanently deleted.";
    const htmlSpaces = "All space property data will be permanently deleted.";
    Swal.fire({
      title: "Are you sure?",
      html: type === "filter-members" ? htmlMembers : htmlSpaces,
      showDenyButton: true,
      confirmButtonText: "Yes",
      denyButtonText: "No",
      confirmButtonColor: colors.red,
      denyButtonColor: colors.gray
    }).then((result) => {
      if (result.isConfirmed && selectedProperty) {
        handlers.remove(allFilters.findIndex((i) => i.id === selectedProperty.id));
        setSelectedProperty(null);
        setActiveTypeLabel(null);
        FilterModel.onDestroy(
          slug,
          selectedProperty.id,
          (data) => {},
          (error) => {}
        );
      } else {
        setSelectedProperty(selectedProperty);
      }
    });
  };

  const handleDuplicate = () => {
    const option = {
      label: selectedProperty.name,
      value: selectedProperty.format,
      options: selectedProperty.options
    };
    handleAddProperty(option);
  };

  const onUpdateOptionsSort = (value) => {
    if (!selectedProperty?.id) return;
    const option_key = getOptionKey(selectedProperty.format);
    const sortedValues = sortByName(selectedProperty.options.values, value);
    const options = {
      ...selectedProperty.options,
      values: sortedValues,
      [option_key]: value
    };
    onFilterUpdate(selectedProperty.id, { options });
    setSelectedProperty({...selectedProperty, options});
  };

  const onUpdateOption = (value, isReorder) => {
    if (!value || !selectedProperty?.id) return;
    let values = [];

    if (Array.isArray(value)) {
      values = value;
    } else {
      const oldValues = Array.isArray(selectedProperty.options?.values) ? selectedProperty.options.values : [];
      const option_name = generateUniqueName(value, oldValues);
      const next_color = getNextAvailableColor(theme, oldValues);
      const newOption = {
        id: generateKeyFromName(option_name),
        value: generateKeyFromName(option_name),
        name: option_name,
        sort_order: oldValues.length,
        visible: true,
        color: next_color
      };
      values = [...oldValues, newOption]
    }
  
    const sort = isReorder ? { sort: "manual" } : {};
    const options = { ...selectedProperty.options, ...sort, values };
    onFilterUpdate(selectedProperty.id, { options });
    setSelectedProperty({...selectedProperty, options});
  };

  const onChangeVisibility = (values) => {
    if (!selectedProperty?.id) return;
    onFilterUpdate(selectedProperty.id, { visibility: values });
    setSelectedProperty({...selectedProperty, visibility: values});
  };

  const onChangeEmojiicon = (value) => {
    if (!selectedProperty?.id) return;
    onFilterUpdate(selectedProperty.id, { emojicon: value });
    setSelectedProperty({...selectedProperty, emojicon: value});
  };

  const onChangeDecimalPlaces = (value) => {
    if (!selectedProperty?.id) return;
    const options = {
      ...selectedProperty.options,
      decimal_places: value
    };
    onFilterUpdate(selectedProperty.id, { options });
    setSelectedProperty({...selectedProperty, options});
  };

  const onChangeAsThumbnail = (value) => {
    if (!selectedProperty?.id) return;
    const options = {
      ...selectedProperty.options,
      as_thumbnail: value
    };
    onFilterUpdate(selectedProperty.id, { options });
    setSelectedProperty({...selectedProperty, options});
  };

  const debouncedSave = useDebounce(({ id, value }) => {
    if (value.length === 0) return;
    const filter_name = generateUniqueName(value, allFilters);
    const filter_key = generateKeyFromName(filter_name);
    onFilterUpdate(id, { name: filter_name, key: filter_key });
  }, 1000);

  return (
    <Modal
      opened={!!selectedProperty}
      onClose={onClose}
      size={250}
      transitionDuration={0}
      padding={0}
      zIndex={9999999}
      overlayOpacity={0.1}
      withCloseButton={false}
      styles={{ inner: { width: 870 } }}
      overflow="inside"
    >
      <Box onClick={(e) => e.stopPropagation()}>
        <Group mb="md" spacing={0} px={10} pt={10}>
          <FilterIconSelector emojicon={selectedProperty?.emojicon} onChangeEmojiicon={onChangeEmojiicon} />
          <TextInput
            value={typeof activeTypeLabel === "string" ? activeTypeLabel : (selectedProperty?.name || "")}
            style={{ flex: 1, marginLeft: 15 }}
            size="xs"
            onChange={(e) => {
              setActiveTypeLabel(e.currentTarget.value);
              debouncedSave({ id: selectedProperty.id, value: e.currentTarget.value });
            }}
          />
        </Group>

        <FilterPropertySelector
          TargetComponent={({ onClick }) => (
            <Group
              position="apart"
              mx={5}
              p={5}
              sx={{ width: 238, cursor: "pointer", borderRadius: 5, "&:hover": { backgroundColor: "#f3f3f3" } }}
              onClick={(e) => {
                e.stopPropagation();
                onClick();
              }}
            >
              <Text size="sm" style={{ flex: 1 }}>Type</Text>
              <Group spacing={4}>
                <Text size="xs" color="grey">
                  {ALL_FILTER_OPTIONS.find((item) => item.value === selectedProperty?.format)?.label}
                </Text>
                <FontAwesomeIcon icon={solid("chevron-right")} size="xs" color="grey" />
              </Group>
            </Group>
          )}
          withinPortal={true}
          slug={slug}
          type={type}
          allFilters={allFilters}
          handlers={handlers}
          selectedProperty={selectedProperty}
          setSelectedProperty={setSelectedProperty}
        />

        {ALL_FILTER_PROPERTIES[selectedProperty?.format]?.sort && (
          <FilterSort
            format={selectedProperty?.format}
            label={ALL_FILTER_PROPERTIES[selectedProperty?.format].label}
            options={ALL_FILTER_PROPERTIES[selectedProperty?.format].sort}
            value={selectedProperty.options?.sort || ALL_FILTER_PROPERTIES[selectedProperty.format].default_value}
            onChange={onUpdateOptionsSort}
          />
        )}

        {ALL_FILTER_PROPERTIES[selectedProperty?.format]?.format && (
          <FilterSort
            isCurrency
            format={selectedProperty?.format}
            label={ALL_FILTER_PROPERTIES[selectedProperty?.format].label}
            options={ALL_FILTER_PROPERTIES[selectedProperty?.format].format}
            value={selectedProperty.options?.format || ALL_FILTER_PROPERTIES[selectedProperty.format].default_value}
            onChange={onUpdateOptionsSort}
          />
        )}
        
        {ALL_FILTER_PROPERTIES[selectedProperty?.format]?.decimalPlaces && (
          <FilterSort
            format={selectedProperty?.format}
            label="Decimal places"
            options={ALL_FILTER_PROPERTIES[selectedProperty?.format].decimalPlaces}
            value={selectedProperty.options?.decimal_places || "default"}
            onChange={onChangeDecimalPlaces}
          />
        )}

        <FilterVisibility
          options={
            ["image", "file"].includes(selectedProperty?.format)
            ? (type === "filter-members" ? ALL_FILTER_MEMBER_FILE_VISIBILITY : ALL_FILTER_SPACE_FILE_VISIBILITY)
            : (type === "filter-members" ? ALL_FILTER_MEMBER_VISIBILITY : ALL_FILTER_SPACE_VISIBILITY)
          }
          values={selectedProperty?.visibility}
          onChange={onChangeVisibility}
        />
        
        {selectedProperty?.format === "image" && (imageFilterWithThumbnail ? imageFilterWithThumbnail.id === selectedProperty?.id : true) && (
          <>
            <Divider my="md" />
            <Switch
              label="Set as thumbnail"
              checked={selectedProperty?.options?.as_thumbnail || false}
              ml={10}
              mt={5}
              styles={{
                input: { cursor: "pointer" },
                label: { cursor: "pointer" },
              }}
              onChange={(e) => {
                e.stopPropagation();
                onChangeAsThumbnail(e.currentTarget.checked);
              }}
            />
          </>
        )}

        {ALL_FILTER_PROPERTIES[selectedProperty?.format]?.limit && (
          <Box mb="xs">
            <Text size="xs" transform="uppercase" weight={600} mb={4}>Limit</Text>
            <NumberInput size="xs" min={1} max={100} defaultValue={10} />
          </Box>
        )}

        {ALL_FILTER_PROPERTIES[selectedProperty?.format]?.show_options && (
          <>
            <Divider my="lg" />
            <FilterOptions
              options={Array.isArray(selectedProperty?.options?.values) ? selectedProperty.options.values : []}
              onUpdateOption={onUpdateOption}
            />
          </>
        )}

        <Divider my="md" />

        <Box px={5} pb={10}>
          <UnstyledButton
            onClick={handleDuplicate}
            p={5}
            sx={{ display: "flex", width: "100%", alignItems: "center", borderRadius: 5, "&:hover": { backgroundColor: "#f3f3f3" } }}
          >
            <FontAwesomeIcon icon="clone" color="grey" width={20} />
            <Text size="sm" color="grey" pl={10}>Duplicate property</Text>
          </UnstyledButton>

          <UnstyledButton
            onClick={handleDelete}
            p={5}
            sx={{ display: "flex", width: "100%", alignItems: "center", borderRadius: 5, "&:hover": { backgroundColor: "#f3f3f3" } }}
          >
            <FontAwesomeIcon icon="trash" color="grey" width={20} />
            <Text size="sm" color="grey" pl={10}>Delete property</Text>
          </UnstyledButton>
        </Box>
      </Box>
    </Modal>
  );
}
